<template>
  <div class="renew-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="renew-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">🔄 Renew Subscription</h1>
              <p class="page-subtitle">Extend your subscription to continue using Quali-D services</p>
            </div>
          </div>

          <!-- Current Subscription Info -->
          <div class="subscription-info-section">
            <h2 class="section-title">Current Subscription</h2>

            <div v-if="loadingSubscription" class="loading-container">
              <div class="loading-spinner"></div>
              <p>Loading subscription details...</p>
            </div>

            <div v-else-if="subscriptionExpired" class="subscription-card expired-notice">
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="status-badge expired">Expired</span>
              </div>
              <div class="info-row">
                <span class="info-label">Note:</span>
                <span class="info-value">Your subscription has ended. Please select a plan below to renew.</span>
              </div>
            </div>

            <div v-else-if="subscriptionError" class="error-container">
              <div class="error-icon">⚠️</div>
              <h3>Error Loading Subscription</h3>
              <p>{{ subscriptionError }}</p>
              <button @click="loadSubscriptionStatus" class="retry-button">Try Again</button>
            </div>

            <div v-else-if="subscription" class="subscription-card">
              <div class="info-row">
                <span class="info-label">Plan:</span>
                <span class="info-value">{{ subscription.plan_name }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Status:</span>
                <span class="status-badge" :class="{ 'active': !subscription.is_time_to_renew, 'renewal-due': subscription.is_time_to_renew }">
                  {{ subscription.is_time_to_renew ? 'Renewal Due' : 'Active' }}
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">Current End Date:</span>
                <span class="info-value">{{ formatDate(subscription.end_date) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Days Remaining:</span>
                <span class="info-value" :class="{ 'urgent': subscription.days_to_renewal <= 7 }">
                  {{ subscription.days_to_renewal }} days
                </span>
              </div>
            </div>
          </div>

          <!-- Renewal Form -->
          <div class="renewal-form-section">
            <h2 class="section-title">Renewal Details</h2>

            <form @submit.prevent="handleRenewal" class="renewal-form">
              <!-- Plan Selection -->
              <div class="form-section">
                <h3 class="form-section-title">Select Plan</h3>

                <div v-if="loadingPlans" class="loading-container">
                  <div class="loading-spinner"></div>
                  <p>Loading available plans...</p>
                </div>

                <div v-else-if="plansError" class="error-container">
                  <div class="error-icon">⚠️</div>
                  <h3>Error Loading Plans</h3>
                  <p>{{ plansError }}</p>
                  <button @click="loadAvailablePlans" class="retry-button">Try Again</button>
                </div>

                <div v-else class="form-group-plans">
                  <label class="form-label">Choose Your Plan</label>

                  <!-- Plan Cards Grid -->
                  <div class="plans-grid">
                    <div
                      v-for="plan in availablePlansFiltered"
                      :key="plan.id"
                      class="plan-card"
                      :class="{
                        'selected': renewalForm.planId === plan.id,
                        'current': subscription && plan.id === subscription.plan_id,
                        'recommended': isRecommendedPlan(plan)
                      }"
                      @click="selectPlan(plan.id)"
                    >
                      <!-- Plan Type Badge -->
                      <div class="plan-badges">
                        <span v-if="plan.plan_type === 'annual'" class="badge badge-annual">
                          💎 ANNUAL - Save 20%
                        </span>
                        <span v-else class="badge badge-monthly">Monthly</span>

                        <span v-if="subscription && plan.id === subscription.plan_id" class="badge badge-current">
                          Current Plan
                        </span>
                        <span v-else-if="isRecommendedPlan(plan)" class="badge badge-recommended">
                          ⭐ Recommended
                        </span>
                      </div>

                      <!-- Plan Name -->
                      <h3 class="plan-name">{{ getPlanDisplayName(plan) }}</h3>

                      <!-- Plan Price -->
                      <div class="plan-price">
                        <span class="price-amount">{{ plan.price.toLocaleString() }}</span>
                        <span class="price-currency">EGP</span>
                        <span class="price-period">/ {{ plan.plan_type === 'annual' ? 'year' : 'month' }}</span>
                      </div>

                      <!-- Monthly Equivalent for Annual Plans -->
                      <div v-if="plan.plan_type === 'annual'" class="monthly-equivalent">
                        Only {{ Math.round(plan.price / 12).toLocaleString() }} EGP/month
                      </div>

                      <!-- Plan Features -->
                      <div class="plan-features">
                        <div class="feature">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          <span>{{ plan.agents_limit === 0 ? 'Unlimited' : plan.agents_limit }} Agents</span>
                        </div>
                        <div class="feature">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          <span>{{ plan.credit_amount.toLocaleString() }} Credits</span>
                        </div>
                        <div v-if="plan.products_services_limit > 0" class="feature">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          <span>{{ plan.products_services_limit }} Products/Services</span>
                        </div>
                        <div v-else class="feature">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          <span>Unlimited Products/Services</span>
                        </div>
                      </div>

                      <!-- Upgrade Incentive -->
                      <div v-if="shouldShowUpgradeIncentive(plan)" class="upgrade-incentive">
                        {{ getUpgradeIncentiveText(plan) }}
                      </div>

                      <!-- Selection Indicator -->
                      <div class="selection-indicator">
                        <div class="radio-button" :class="{ 'checked': renewalForm.planId === plan.id }">
                          <div class="radio-inner"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <span v-if="renewalErrors.planId" class="error-message">{{ renewalErrors.planId }}</span>
                </div>
              </div>

              <!-- Payment Information -->
              <div class="form-section">
                <h3 class="form-section-title">Payment Information</h3>

                <div class="form-group">
                  <label for="cardNumber" class="form-label">Card Number</label>
                  <div class="input-wrapper">
                    <svg class="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                    <input
                      id="cardNumber"
                      v-model="renewalForm.cardNumber"
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      class="form-input"
                      :class="{ 'error': renewalErrors.cardNumber }"
                      maxlength="19"
                      @input="formatCardNumber"
                      required
                    />
                  </div>
                  <span v-if="renewalErrors.cardNumber" class="error-message">{{ renewalErrors.cardNumber }}</span>
                </div>

                <div class="form-row">
                  <div class="form-group">
                    <label for="expiryMonth" class="form-label">Expiry Month</label>
                    <select
                      id="expiryMonth"
                      v-model="renewalForm.expiryMonth"
                      class="form-input"
                      :class="{ 'error': renewalErrors.expiryMonth }"
                      required
                    >
                      <option value="">Month</option>
                      <option v-for="month in months" :key="month.value" :value="month.value">
                        {{ month.label }}
                      </option>
                    </select>
                    <span v-if="renewalErrors.expiryMonth" class="error-message">{{ renewalErrors.expiryMonth }}</span>
                  </div>

                  <div class="form-group">
                    <label for="expiryYear" class="form-label">Expiry Year</label>
                    <select
                      id="expiryYear"
                      v-model="renewalForm.expiryYear"
                      class="form-input"
                      :class="{ 'error': renewalErrors.expiryYear }"
                      required
                    >
                      <option value="">Year</option>
                      <option v-for="year in years" :key="year" :value="year">
                        {{ year }}
                      </option>
                    </select>
                    <span v-if="renewalErrors.expiryYear" class="error-message">{{ renewalErrors.expiryYear }}</span>
                  </div>

                  <div class="form-group">
                    <label for="cvv" class="form-label">CVV</label>
                    <input
                      id="cvv"
                      v-model="renewalForm.cvv"
                      type="password"
                      placeholder="123"
                      class="form-input"
                      :class="{ 'error': renewalErrors.cvv }"
                      maxlength="4"
                      required
                    />
                    <span v-if="renewalErrors.cvv" class="error-message">{{ renewalErrors.cvv }}</span>
                  </div>
                </div>

                <div v-if="renewalError" class="renewal-error">
                  {{ renewalError }}
                </div>
              </div>

              <!-- Submit Button -->
              <div class="form-actions">
                <button type="submit" class="submit-button" :disabled="isRenewing || !isRenewalFormValid">
                  <span v-if="!isRenewing">💳 Proceed to Payment</span>
                  <span v-else class="loading-text">
                    <span class="spinner"></span>
                    Processing...
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { PaymentsService } from '../services/payments.js'

export default {
  name: 'RenewSubscriptionView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      subscription: null,
      subscriptionExpired: false,
      availablePlans: [],
      loadingSubscription: false,
      loadingPlans: false,
      subscriptionError: null,
      plansError: null,
      renewalForm: {
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        planId: null
      },
      renewalErrors: {},
      renewalError: '',
      isRenewing: false,
      months: [
        { value: '01', label: '01 - January' },
        { value: '02', label: '02 - February' },
        { value: '03', label: '03 - March' },
        { value: '04', label: '04 - April' },
        { value: '05', label: '05 - May' },
        { value: '06', label: '06 - June' },
        { value: '07', label: '07 - July' },
        { value: '08', label: '08 - August' },
        { value: '09', label: '09 - September' },
        { value: '10', label: '10 - October' },
        { value: '11', label: '11 - November' },
        { value: '12', label: '12 - December' }
      ]
    }
  },
  computed: {
    years () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = 0; i < 15; i++) {
        years.push((currentYear + i).toString())
      }
      return years
    },

    // Filter plans to prevent downgrades
    availablePlansFiltered () {
      if (!this.subscription || !this.subscription.plan_id || this.availablePlans.length === 0) {
        return this.availablePlans
      }

      const currentPlan = this.availablePlans.find(p => p.id === this.subscription.plan_id)
      if (!currentPlan) {
        return this.availablePlans
      }

      // Define plan tier hierarchy
      const getTier = (planName) => {
        const name = planName.toLowerCase()
        if (name.includes('trial')) return 0
        if (name.includes('basic')) return 1
        if (name.includes('starter')) return 2
        if (name.includes('pro')) return 3
        return 1 // Default to basic tier
      }

      const currentTier = getTier(currentPlan.name)

      // Filter to only show plans at same tier or higher
      return this.availablePlans.filter(plan => {
        // Allow same plan (renewal)
        if (plan.id === currentPlan.id) {
          return true
        }

        const planTier = getTier(plan.name)

        // Must be same tier or higher
        if (planTier < currentTier) {
          return false
        }

        // If same tier, allow both monthly and annual
        if (planTier === currentTier) {
          return true
        }

        // If higher tier, allow it
        return true
      }).sort((a, b) => {
        // Sort by tier first, then by price
        const tierA = getTier(a.name)
        const tierB = getTier(b.name)
        if (tierA !== tierB) {
          return tierA - tierB
        }
        return a.price - b.price
      })
    },

    isRenewalFormValid () {
      const form = this.renewalForm
      return form.planId &&
             form.cardNumber.trim() &&
             form.cardNumber.replace(/\s/g, '').length >= 13 &&
             form.expiryMonth &&
             form.expiryYear &&
             form.cvv &&
             form.cvv.length >= 3 && form.cvv.length <= 4
    }
  },
  async mounted () {
    await Promise.all([
      this.loadSubscriptionStatus(),
      this.loadAvailablePlans()
    ])
    // Pre-select current plan after both have loaded
    if (this.subscription && this.subscription.plan_id && !this.renewalForm.planId) {
      this.renewalForm.planId = this.subscription.plan_id
    }
  },
  methods: {
    async loadSubscriptionStatus () {
      this.loadingSubscription = true
      this.subscriptionError = null
      this.subscriptionExpired = false

      try {
        const result = await PaymentsService.getSubscriptionStatus()
        console.log('Subscription status response:', result)

        if (!result.success) {
          // Expired subscription is a valid state on this page — show graceful UI
          this.subscriptionExpired = true
          // Still store the last plan data if the backend returned it (for pre-selection)
          if (result.subscription && result.subscription.plan_id) {
            this.subscription = result.subscription
          }
          return
        }

        this.subscription = result.subscription
      } catch (error) {
        console.error('Error loading subscription status:', error)
        this.subscriptionError = error.message || 'Failed to load subscription status. Please try again.'
      } finally {
        this.loadingSubscription = false
      }
    },

    async loadAvailablePlans () {
      this.loadingPlans = true
      this.plansError = null

      try {
        const result = await PaymentsService.getAvailablePlans()
        console.log('Available plans response:', result)

        if (!result.success) {
          throw new Error(result.message || 'Failed to load available plans')
        }

        this.availablePlans = result.plans
      } catch (error) {
        console.error('Error loading available plans:', error)
        this.plansError = error.message || 'Failed to load available plans. Please try again.'
      } finally {
        this.loadingPlans = false
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'Not provided'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },

    formatCardNumber () {
      let value = this.renewalForm.cardNumber.replace(/\D/g, '')
      value = value.replace(/(\d{4})(?=\d)/g, '$1 ')
      this.renewalForm.cardNumber = value
    },

    selectPlan (planId) {
      this.renewalForm.planId = planId
      this.renewalErrors.planId = ''
    },

    getPlanDisplayName (plan) {
      // Remove price from name if it exists
      return plan.name.replace(/\s*\(\d+[,\d]*\s*EGP\)\s*/i, '')
    },

    isRecommendedPlan (plan) {
      if (!this.subscription) return false

      const currentPlan = this.availablePlans.find(p => p.id === this.subscription.plan_id)
      if (!currentPlan) return false

      // Recommend annual version of monthly plans
      if (currentPlan.plan_type === 'monthly' && plan.plan_type === 'annual') {
        // Check if it's the annual version of a similar tier
        const currentName = currentPlan.name.toLowerCase()
        const planName = plan.name.toLowerCase()

        if (currentName.includes('basic') && planName.includes('basic')) return false
        if (currentName.includes('starter') && planName.includes('starter')) return true
        if (currentName.includes('pro') && planName.includes('pro')) return true
      }

      // Recommend Starter for Basic users
      if (currentPlan.name.toLowerCase().includes('basic') && plan.name.toLowerCase().includes('starter')) {
        return true
      }

      // Recommend Pro for Starter users
      if (currentPlan.name.toLowerCase().includes('starter') && plan.name.toLowerCase().includes('pro')) {
        return true
      }

      return false
    },

    shouldShowUpgradeIncentive (plan) {
      if (!this.subscription) return false
      if (plan.id === this.subscription.plan_id) return false

      return this.isRecommendedPlan(plan) || this.getUpgradeIncentiveText(plan) !== ''
    },

    getUpgradeIncentiveText (plan) {
      if (!this.subscription) return ''

      const currentPlan = this.availablePlans.find(p => p.id === this.subscription.plan_id)
      if (!currentPlan) return ''

      // Annual plan incentive
      if (currentPlan.plan_type === 'monthly' && plan.plan_type === 'annual') {
        const monthlyCost = currentPlan.price * 12
        const savings = monthlyCost - plan.price
        if (savings > 0) {
          return `💰 Save ${savings.toLocaleString()} EGP per year!`
        }
      }

      // Tier upgrade incentives
      const currentName = currentPlan.name.toLowerCase()
      const planName = plan.name.toLowerCase()

      if (currentName.includes('basic')) {
        if (planName.includes('starter')) {
          return '🚀 2x more agents & 2x more credits!'
        }
        if (planName.includes('pro')) {
          return '⚡ 3x more agents & unlimited products!'
        }
      }

      if (currentName.includes('starter')) {
        if (planName.includes('pro')) {
          return '💪 50% more agents & 3x more credits!'
        }
      }

      return ''
    },

    validateRenewalForm () {
      this.renewalErrors = {}
      let isValid = true

      if (!this.renewalForm.planId) {
        this.renewalErrors.planId = 'Please select a plan'
        isValid = false
      }

      if (!this.renewalForm.cardNumber.trim()) {
        this.renewalErrors.cardNumber = 'Card number is required'
        isValid = false
      } else if (this.renewalForm.cardNumber.replace(/\s/g, '').length < 13) {
        this.renewalErrors.cardNumber = 'Please enter a valid card number'
        isValid = false
      }

      if (!this.renewalForm.expiryMonth) {
        this.renewalErrors.expiryMonth = 'Expiry month is required'
        isValid = false
      }

      if (!this.renewalForm.expiryYear) {
        this.renewalErrors.expiryYear = 'Expiry year is required'
        isValid = false
      }

      if (!this.renewalForm.cvv) {
        this.renewalErrors.cvv = 'CVV is required'
        isValid = false
      } else if (this.renewalForm.cvv.length < 3 || this.renewalForm.cvv.length > 4) {
        this.renewalErrors.cvv = 'CVV must be 3-4 digits'
        isValid = false
      }

      return isValid
    },

    async handleRenewal () {
      this.renewalError = ''

      if (!this.validateRenewalForm()) {
        return
      }

      // Additional validation: Ensure selected plan is in filtered list (prevents downgrade attempts)
      const selectedPlanInFiltered = this.availablePlansFiltered.find(p => p.id === this.renewalForm.planId)
      if (!selectedPlanInFiltered) {
        this.renewalError = 'The selected plan is not available for renewal. Please choose a valid plan.'
        return
      }

      this.isRenewing = true

      try {
        const selectedPlan = this.availablePlans.find(plan => plan.id === this.renewalForm.planId)
        const amount = selectedPlan ? selectedPlan.price.toString() : '0'

        const renewalData = {
          cardNumber: this.renewalForm.cardNumber.replace(/\s/g, ''),
          expiryYear: this.renewalForm.expiryYear.slice(-2),
          expiryMonth: this.renewalForm.expiryMonth,
          cvv: this.renewalForm.cvv,
          plan_id: this.renewalForm.planId,
          amount: amount
        }

        console.log('Sending renewal data:', renewalData)

        const result = await PaymentsService.initiateRenewal(renewalData)
        console.log('Renewal response:', result)

        if (!result.success) {
          throw new Error(result.message || 'Renewal failed')
        }

        if (result.redirectUrl && result.merchantRefNumber) {
          localStorage.setItem('merchantRefNum', result.merchantRefNumber)

          this.$router.push({
            name: 'payment',
            query: {
              url: result.redirectUrl,
              merchantRefNum: result.merchantRefNumber,
              renewal: 'true'
            }
          })
        } else {
          throw new Error('No payment URL or merchant reference number received')
        }
      } catch (error) {
        console.error('Renewal error:', error)
        this.renewalError = error.message || 'Renewal failed. Please try again.'
      } finally {
        this.isRenewing = false
      }
    }
  }
}
</script>

<style scoped>
/* Layout Structure */
.renew-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 20px;
}

.renew-container {
  max-width: 800px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
  padding: 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.15);
}

.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 0 0 6px;
}

.header-content p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

/* Section Styles */
.subscription-info-section,
.renewal-form-section {
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border-primary);
}

/* Subscription Card */
.subscription-card {
  background: var(--bg-secondary);
  padding: 20px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-primary);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.info-value.urgent {
  color: #dc2626;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.status-badge.renewal-due {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}

.status-badge.expired {
  background: rgba(220, 38, 38, 0.15);
  color: #dc2626;
}

.expired-notice {
  border-color: rgba(220, 38, 38, 0.35);
  background: rgba(220, 38, 38, 0.07);
}

.expired-notice .info-label {
  color: var(--text-secondary);
}

.expired-notice .info-value {
  color: var(--text-primary);
}

/* Form Styles */
.renewal-form {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Plan Selection Styles */
.form-group-plans {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 12px;
}

.plan-card {
  position: relative;
  background: var(--bg-primary);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.plan-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.plan-card.selected {
  border-color: #3b82f6;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(29, 78, 216, 0.05) 100%);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
}

.plan-card.current {
  border-color: #10b981;
}

.plan-card.recommended {
  border-color: #f59e0b;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.2);
}

.plan-card.recommended::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  border-radius: 12px;
  z-index: -1;
  opacity: 0.3;
}

/* Plan Badges */
.plan-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-annual {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 8px rgba(245, 158, 11, 0.4);
  }
  50% {
    box-shadow: 0 0 16px rgba(245, 158, 11, 0.6);
  }
}

.badge-monthly {
  background: rgba(107, 114, 128, 0.15);
  color: #6b7280;
}

.badge-current {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
}

.badge-recommended {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
}

/* Plan Name */
.plan-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

/* Plan Price */
.plan-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin: 8px 0;
}

.price-amount {
  font-size: 32px;
  font-weight: 800;
  color: #3b82f6;
  line-height: 1;
}

.price-currency {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-secondary);
}

.price-period {
  font-size: 14px;
  color: var(--text-secondary);
}

.monthly-equivalent {
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  padding: 6px 12px;
  border-radius: 6px;
  text-align: center;
  margin-top: -8px;
}

/* Plan Features */
.plan-features {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 0;
  border-top: 1px solid var(--border-primary);
  border-bottom: 1px solid var(--border-primary);
}

.feature {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--text-primary);
}

.feature svg {
  color: #10b981;
  flex-shrink: 0;
}

/* Upgrade Incentive */
.upgrade-incentive {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #1e40af;
  text-align: center;
  margin-top: 4px;
}

/* Selection Indicator */
.selection-indicator {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}

.radio-button {
  width: 24px;
  height: 24px;
  border: 2px solid var(--border-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.radio-button.checked {
  border-color: #3b82f6;
  background: #3b82f6;
}

.radio-inner {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: white;
  transform: scale(0);
  transition: transform 0.2s ease;
}

.radio-button.checked .radio-inner {
  transform: scale(1);
}

.form-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-primary);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 16px;
  color: var(--text-secondary);
  opacity: 0.6;
  z-index: 2;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
  outline: none;
}

.input-wrapper .form-input {
  padding-left: 48px;
}

.form-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.error {
  border-color: #ef4444;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.error-message {
  font-size: 12px;
  color: #ef4444;
  margin-top: 2px;
}

.info-message {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 12px;
  line-height: 1.5;
}

.renewal-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}

.submit-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 200px;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  background: #9ca3af;
}

.loading-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Loading and Error States */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid var(--border-primary);
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

.loading-container p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.error-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.error-container h3 {
  color: var(--text-primary);
  margin: 0 0 6px;
  font-size: 16px;
}

.error-container p {
  color: var(--text-secondary);
  margin: 0 0 16px;
  font-size: 14px;
}

.retry-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.retry-button:hover {
  background: #2563eb;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 12px;
  }

  .page-header {
    padding: 20px;
    margin-bottom: 16px;
  }

  .header-content h1 {
    font-size: 24px;
  }

  .header-content p {
    font-size: 13px;
  }

  .subscription-info-section,
  .renewal-form-section {
    padding: 20px;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 16px;
  }

  .plans-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .plan-card {
    padding: 20px;
  }

  .plan-name {
    font-size: 18px;
  }

  .price-amount {
    font-size: 28px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .form-actions {
    justify-content: stretch;
  }

  .submit-button {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .page-content {
    padding: 8px;
  }

  .page-header {
    padding: 16px;
  }

  .header-content h1 {
    font-size: 20px;
  }

  .subscription-info-section,
  .renewal-form-section {
    padding: 16px;
  }

  .info-row {
    padding: 10px 0;
  }

  .info-label,
  .info-value {
    font-size: 13px;
  }
}
</style>
